import { useCallback } from 'react'
import type { MouseEvent } from 'react'

import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { getCurrentTenantSlug } from '@/shared/lib/tenant/current'
import { resolveTenantSlug } from '@/shared/lib/tenant/resolve'
import { resolveInternalPath } from '@/shared/lib/url/internal-link'

const isPlainLeftClick = (event: MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey

/**
 * Handler de clique para links vindos de conteúdo do produtor. Link do próprio
 * evento navega pelo roteador; qualquer outro segue o `<a>` normalmente.
 */
export const useInternalLinkClick = () => {
  const router = useAppRouter()

  const pathFor = useCallback((href: string | null | undefined) => {
    if (typeof window === 'undefined') return null
    return resolveInternalPath(href, {
      currentHost: window.location.host,
      currentTenantSlug: getCurrentTenantSlug(),
      resolveSlug: resolveTenantSlug
    })
  }, [])

  /** Para `<a href>` direto: intercepta se o link do `href` for interno. */
  const onLinkClick = useCallback(
    (event: MouseEvent, href: string | null | undefined) => {
      if (event.defaultPrevented || !isPlainLeftClick(event)) return
      const path = pathFor(href)
      if (!path) return
      event.preventDefault()
      router.push(path)
    },
    [pathFor, router]
  )

  /** Para HTML injetado: delega ao `<a>` mais próximo do alvo do clique. */
  const onContentClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as HTMLElement).closest?.('a')
      if (!anchor) return
      onLinkClick(event, anchor.getAttribute('href'))
    },
    [onLinkClick]
  )

  return { onLinkClick, onContentClick, isInternal: (href?: string | null) => pathFor(href) !== null }
}
